import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

// ── Pay Periods ──────────────────────────────────────────────────────────────

export function usePayPeriods() {
  return useQuery({
    queryKey: queryKeys.payPeriods.all,
    queryFn: async () => {
      const data = await api.get('/pay-periods');
      return data.map(mapFromDb);
    },
  });
}

export function useCreatePayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newPeriod) => {
      const data = await api.post('/pay-periods', mapToDb(newPeriod));
      return mapFromDb(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.all });
      // Archival is the only path here; PayPeriodManager logs "Archive Pay Period"
      // with the rich context (totals, ledger rows locked) this hook can't see.
    },
  });
}

export function useUpdatePayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (period) => {
      const { id, ...updates } = period;
      const data = await api.put(`/pay-periods/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.all });
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.detail(data.id) });
      logAction({
        action: 'Update Pay Period',
        details: { status: data.status },
      });
    },
  });
}

export function useDeletePayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (periodId) => {
      await api.del(`/pay-periods/${periodId}`);
    },
    onSuccess: (_data, periodId) => {
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.all });
      logAction({
        action: 'Delete Pay Period',
        details: { periodId },
      });
    },
  });
}

// ── Pay Period Payments ──────────────────────────────────────────────────────

export function useCreatePayPeriodPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payment) => {
      const data = await api.post('/pay-period-payments', mapToDb(payment));
      return mapFromDb(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payPayments.all });
    },
  });
}

export function usePayPeriodPayments() {
  return useQuery({
    queryKey: queryKeys.payPayments.all,
    queryFn: async () => {
      const data = await api.get('/pay-period-payments');
      return data.map(mapFromDb);
    },
  });
}

export function useUpdatePayPeriodPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payment) => {
      const { id, ...updates } = payment;
      const data = await api.put(`/pay-period-payments/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payPayments.all });
      // Logging lives in the caller (CrewAccounting / PayPeriodManager): the same
      // update serves Log Payment / Adjust / Reopen / Set Actual, so only the
      // handler knows the real semantic action + details.
    },
  });
}

export function useDeletePayPeriodPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => { await api.del(`/pay-period-payments/${id}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payPayments.all });
      // Logged by the caller with person/amount/items-returned context.
    },
  });
}
